import { FC, useCallback, useEffect, useRef, useState } from "react"
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins"
import { DocumentType } from "../../../@types/enumsGlobal"
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel"
import useGridFilter, { GridFilterAction } from "../../../system/hooks/useGridFilter";
import { GoodsClassifierProvider } from "../../../Services/DataProviders/GoodsClassifierDataProvider";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { Spinner } from "../../../components/spiner/Spinner";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { IGoodsClassifierViewDTO } from "../../../libs/coreapi-dto/dirs/goodsClassifier";
import { GoodsClassifierCreatorModal } from "./components/GoodsClassifierCreatorModal";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { TabsPanel } from "../../../components/tabs";
import classNames from "classnames";
import { usePluginContext } from "../../../system/providers/plugin";
import { TreeGrid } from "../../../components/grids/treeGrid/treeGrid";

export const GoodsClassifierPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const goodsClassifierDataProvider = new GoodsClassifierProvider(appContext.coreApiService);
    const pluginContext = usePluginContext();

    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>();
    const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
    const [totalCount, setTotalCount] = useState(0);
    const [data, setData] = useState<IGoodsClassifierViewDTO[]>([]);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);

    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const loadedChildrenRef = useRef<Set<string>>(new Set());

    const CreateGoodsClassifierGridFilter: IGridFilter = {
        numberPerPage: 10000,
        pageNumber: 1,
        columnFilters: [
            {
                name: "isTopLevel",
                operator: "Eq",
                value: true,
                invisible: true,
            },
            {
                name: "deleted",
                operator: "Eq",
                value: false,
                invisible: true,
            },
        ]
    };
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateGoodsClassifierGridFilter);

    const dispatchGridFilterWithSearchFix: React.Dispatch<GridFilterAction> = useCallback((action) => {
        if (action.type === 'search') {
            const filters = (action.payload ?? []).filter(f => f.name !== 'isTopLevel');

            const userFilters = filters.filter(f => f.name !== 'deleted');

            // Нет пользовательских фильтров → добавляем isTopLevel: true (иерархия).
            // Есть пользовательские → isTopLevel не добавляем (поиск везде).
            const payload = userFilters.length === 0
                ? [
                    ...filters,
                    { name: 'isTopLevel', operator: 'Eq' as OperatorType, value: true },
                ]
                : filters;

            dispatchGridFilter({ ...action, payload });
            return;
        }

        dispatchGridFilter(action);
    }, [dispatchGridFilter]);

    const isFlatMode = gridFilter.columnFilters?.some(
        filter =>
            filter.name !== "isTopLevel" &&
            filter.name !== "deleted"
    ) ?? false;

    const loadChildren = (parentId: number | null, parentGlobalId: string) => {
        if (!parentId) return;

        if (loadedChildrenRef.current.has(parentGlobalId)) {
            setLoadingNodes(prev => {
                const next = new Set(prev);
                next.delete(parentGlobalId);
                return next;
            });
            return;
        }

        const currentFilter = gridFilter;
        const filteredColumnFilters = currentFilter.columnFilters?.filter(
            f => f.name !== "isTopLevel"
        ) || [];

        const filter: IGridFilter = {
            numberPerPage: 10000,
            pageNumber: 1,
            columnFilters: [
                ...filteredColumnFilters,
                {
                    name: "IdPharmaDataGoodsCategoryParent",
                    operator: "Eq",
                    value: String(parentId),
                },
            ],
        };

        goodsClassifierDataProvider.getView(filter, (childrenData, childrenTotalCount) => {
            setData(prev => {
                const existingIds = new Set(prev.map(item => item.idGlobal));
                const newItems = childrenData
                    .filter(item => !existingIds.has(item.idGlobal))
                    .map(item => ({
                        ...item,
                        hasChildren: item.hasChildrenPharmaDataGoodsCategory || false,
                    }));

                loadedChildrenRef.current.add(parentGlobalId);

                return [...prev, ...newItems];
            });
            setTotalCount(prev => prev + childrenTotalCount);
            setLoadingStatus(LoadingStatus.Completed);
            setLoadingNodes(prev => {
                const next = new Set(prev);
                next.delete(parentGlobalId);
                return next;
            });
        });
    };

    const toggleExpand = useCallback((id: string) => {
        if (isFlatMode) {
            return;
        }
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
                setLoadingNodes(prevLoading => new Set(prevLoading).add(id));
                // При раскрытии загружаем детей, если они еще не загружены
                const item = data.find(d => d.idGlobal === id);
                if (item?.hasChildrenPharmaDataGoodsCategory) {
                    loadChildren(item.idPharmaDataGoodsCategory, id);
                }
            }
            return next;
        });
    }, [data, loadChildren, isFlatMode]);

    // Функция сортировки: сначала элементы с детьми, потом остальные
    const sortItems = useCallback((items: IGoodsClassifierViewDTO[]): IGoodsClassifierViewDTO[] => {
        return [...items].sort((a, b) => {
            // Сначала сортируем по наличию детей
            const aHasChildren = a.hasChildrenPharmaDataGoodsCategory || false;
            const bHasChildren = b.hasChildrenPharmaDataGoodsCategory || false;

            // Если у одного есть дети, а у другого нет - ставим с детьми выше
            if (aHasChildren && !bHasChildren) return -1;
            if (!aHasChildren && bHasChildren) return 1;

            // Если оба имеют детей или оба не имеют - сортируем по имени
            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB);
        });
    }, []);

    const changeTreeData = useCallback((items: IGoodsClassifierViewDTO[]): any[] => {
        if (!items || items.length === 0) return [];

        const sortedItems = sortItems(items);

        return sortedItems.map(item => ({
            ...item,
            hasChildren: item.hasChildrenPharmaDataGoodsCategory || false,
        }));
    }, [sortItems]);

    useEffect(() => {
        setIsSubmitting(true);
        setExpandedNodes(new Set());
        setSelectedItem(undefined)
        loadedChildrenRef.current = new Set();

        goodsClassifierDataProvider.getView(gridFilter, (data, totalCount) => {
            setData(data.map(item => ({
                ...item,
                hasChildren: item.hasChildrenPharmaDataGoodsCategory || false,
            })));
            setTotalCount(totalCount);
            setLoadingStatus(LoadingStatus.Completed);
            setIsSubmitting(false);
        });
    }, [gridFilter]);

    useEffect(() => {
        if (viewState === 'refresh') {
            dispatchGridFilter({
                type: 'setGridFilter',
                payload: CreateGoodsClassifierGridFilter,
            });
            setViewState('view');
        }
    }, [viewState]);

    const handleSelect = (row: IGridRow | undefined) => {
        setSelectedItem(row);
        if (row) {
            const item = data.find(d => d.idGlobal === row.idGlobal);
            if (!isFlatMode && item?.hasChildrenPharmaDataGoodsCategory) {
                setExpandedNodes(prev => {
                    const next = new Set(prev);
                    if (!next.has(row.idGlobal)) {
                        next.add(row.idGlobal);
                        loadChildren(item.idPharmaDataGoodsCategory, row.idGlobal);
                    }
                    return next;
                });
            }
            pluginContext.masterGrid.onSelectEvent(row as IGridRow, DocumentType.goodsClassifier);
        } else {
            pluginContext.masterGrid.onUnselectEvent();
        }
        props.onSelect?.(row);
    };

    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <DefaultDictionaryCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilterWithSearchFix]}
                    dataProvider={goodsClassifierDataProvider}
                    creatorModalJsx={GoodsClassifierCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    hiddenCopyButton={true}
                />
            </ToolbarWrapper>
            {
                isSubmitting ? (
                    <Spinner />
                ) : (
                    <>
                        <TreeGrid
                            showQuantity
                            gridId={props.gridId}
                            data={data}
                            loadingStatus={loadingStatus}
                            loadingNodes={loadingNodes}
                            setLoadingStatus={setLoadingStatus}
                            totalCount={totalCount}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            idParent="idPharmaDataGoodsCategory"
                            idChildren="idPharmaDataGoodsCategoryParent"
                            changeData={changeTreeData}
                            expandedIds={expandedNodes}
                            onToggleExpand={toggleExpand}
                            onSelect={handleSelect}
                            onEnter={(e) => console.log('e', e)}
                            nodeIdField="idGlobal"
                            onDoubleClick={(i) => { }}
                            separator
                            autoExpandOnLoad={false}
                            flat={isFlatMode}
                        />
                        {
                            <>
                                {selectedItem && (
                                    <>
                                        <div className={tabsStyles.tabsPanelWrapper}>
                                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                                <TabsPanel
                                                    id="detailsTabsPanel"
                                                    activeId={detailsTabsPanel.currentTab?.id}
                                                    tabs={detailsTabsPanel.tabs}
                                                    onActive={(id) => {
                                                        dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                    }}
                                                ></TabsPanel>
                                            </div>
                                        </div>
                                        <div className={tabsStyles.contentWrapper}>
                                            {detailsTabsPanel.tabs.map((item) => {
                                                return (
                                                    <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                        {item.view.content}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </>
                        }
                    </>
                )
            }
        </PluginWrapper>
    )
}